import { FC, ReactNode } from 'react';
import { shallow } from 'zustand/shallow';

import { CHECKOUT_PATH, PDP_PRICE_CARD_ID } from 'src/constants/constants';
import { productDetailPayloadToBaseCarData } from 'src/data/ProductApi/ProductDetailPayloadToBaseCarData';
import { Button } from 'src/general/components/Button/Button';
import { formatNumber } from 'src/general/helpers/formatNumber';
import { CarDisplayState } from 'src/pdp/sections/Pdp/pdpHelpers';
import { authStore } from 'src/stores/authStore';
import { pdpStore } from 'src/stores/pdpStore';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';
import { DataLayerPdpEvent, pushToDataLayerPDP } from 'src/utils/pushToDataLayerPDP';
import { carDataToDataLayerItem } from 'src/utils/pushToDataLayerProductCard';

import { CarPriceCardDetails } from './CarPriceCardDetails';

interface Props {
  displayState: CarDisplayState;
  productDetails: ProductDetailsPayload;
  isCheckoutProgress: boolean;
  microcopy: {
    startCheckoutDefault: ReactNode;
    continueCheckout: ReactNode;
    notAvailable: ReactNode;
    carPurchased: ReactNode;
    purchaseInfo: ReactNode;
    financeData: ReactNode;
    govtCharges: ReactNode;
    footnote: ReactNode;
  };
}

export const enum PRICE_CARD_IDS {
  PRICE = 'carCardPriceDescription',
  FINANCE = 'carCardFinanceDescription',
}

export const CarPriceCard: FC<Props> = ({ displayState, productDetails, isCheckoutProgress, microcopy }) => {
  const { setCurrentModal } = pdpStore(
    ({ setCurrentModal }) => ({
      setCurrentModal,
    }),
    shallow,
  );
  const carData = productDetailPayloadToBaseCarData(productDetails);
  const { isUserLoggedIn } = authStore(
    (state) => ({
      isUserLoggedIn: state.isUserLoggedIn,
    }),
    shallow,
  );

  const href = isCheckoutProgress
    ? `${CHECKOUT_PATH}?sku=${productDetails.sku}`
    : `/product-detail/${productDetails.sku}/get-started`;

  const onStartCheckout = () => {
    pushToDataLayer({
      event: 'add_to_cart',
      logged_in: isUserLoggedIn,
      ecommerce: {
        items: [carDataToDataLayerItem(carData)],
      },
    });
  };
  const onDisplayGovtCharges = () => {
    setCurrentModal({ name: 'governmentCharges' });
    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku: productDetails.sku,
      option: 'PRICE_GUIDE',
    });
  };
  const onOpenCarEnquiryForm = () => {
    setCurrentModal({ name: 'enquiry' });
    pushToDataLayer({
      event: 'lead_form_start',
      ecommerce: {
        items: [carDataToDataLayerItem(carData)],
      },
      entry_point: 'price_card',
    });
  };

  return (
    <aside
      id={PDP_PRICE_CARD_ID}
      className="relative overflow-hidden rounded-lg border border-bor-neu-mut bg-bac-tie1 text-center shadow-elevation-0dp"
      data-testid="use-finance-tab"
    >
      <header className="border-b border-bor-neu-mut bg-bac-tie2 px-4 py-3">
        <h1 className="c-fs-h5 font-extrabold" data-testid="CarPriceCard-component-car-title">
          {carData.model}
        </h1>
        <p className="c-fs-caption" data-testid="CarPriceCard-component-car-subtitle">
          {carData.variant}
        </p>
        <CarPriceCardDetails carData={carData} />
      </header>
      <section className="flex flex-col gap-4 p-4">
        {displayState === 'my-purchase' ? (
          <>
            <p className="c-fs-h5 font-extrabold" data-testid="CarPriceCard-component-car-purchased">
              {microcopy.carPurchased}
            </p>
            <Button href="/account/purchases" fullWidth data-testid="CarPriceCard-component-purchase-info">
              {microcopy.purchaseInfo}
            </Button>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <h2
                className="c-fs-h5 font-extrabold"
                title="Total price"
                aria-describedby={PRICE_CARD_IDS.PRICE}
                data-testid="CarPriceCard-component-price"
              >
                {formatNumber(carData.price, { prepend: '$', append: '*' })}
              </h2>
              <div
                className="c-fs-caption"
                aria-describedby={PRICE_CARD_IDS.FINANCE}
                data-testid="CarPriceCard-component-finance-description"
              >
                {microcopy.financeData}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {(displayState === 'available' || displayState === 'continue-purchase' || displayState === 'loading') && (
                <Button
                  prefetch={false}
                  onClick={onStartCheckout}
                  href={displayState === 'loading' ? undefined : href}
                  isLoading={displayState === 'loading'}
                  isDisabledWhileLoading
                  fullWidth
                  variant="accent"
                  data-testid="CarPriceCard-component-get-started"
                >
                  {displayState === 'continue-purchase' ? microcopy.continueCheckout : microcopy.startCheckoutDefault}
                </Button>
              )}
              {displayState === 'not-available' && (
                <Button
                  href={`/product-detail/${carData.sku}/similar`}
                  fullWidth
                  data-testid="CarPriceCard-component-view-similars"
                >
                  {microcopy.notAvailable}
                </Button>
              )}
              <Button
                fullWidth
                variant="secondary"
                size="small"
                onClick={onOpenCarEnquiryForm}
                data-testid="CAR_ENQUIRY_BLOCK_CONTENT_BUTTON"
              >
                Send us a message
              </Button>
            </div>
            <footer className="flex flex-col gap-0.5">
              <Button
                variant="link"
                size="small"
                onClick={onDisplayGovtCharges}
                id={PRICE_CARD_IDS.PRICE}
                data-testid={PRICE_CARD_IDS.PRICE}
              >
                *{microcopy.govtCharges}
              </Button>
              <p className="c-fs-caption text-tex-def-tie2" id={PRICE_CARD_IDS.FINANCE}>
                {microcopy.footnote}
              </p>
            </footer>
          </>
        )}
      </section>
    </aside>
  );
};
